import { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Cell } from "recharts";
import { useModuleData } from "@/lib/useData";
import { PALETTE, COLORS, fmt, uniq } from "@/lib/api";
import { PageHeader, Panel, Kpi, FilterSelect, ChartTooltip, Segmented, Loading } from "@/components/dash";
import { DataTable } from "@/components/DataTable";
import { AIInsights } from "@/components/AIInsights";
import { ProductGate } from "@/components/ProductGate";

function PricesView({ product, selector }) {
  const { data, isLoading } = useModuleData("prices", product);
  const rows = data?.rows || [];
  const [freq, setFreq] = useState("Monthly");
  const [category, setCategory] = useState("Chemical");
  const [commodity, setCommodity] = useState(null);

  const freqs = uniq(rows.map((r) => r.frequency));
  const inCat = rows.filter((r) => r.category === category);
  const commodities = uniq(inCat.map((r) => r.commodity));
  const sel = category === "Chemical" ? product : commodities.includes(commodity) ? commodity : commodities[0];

  const series = useMemo(() => rows.filter((r) => r.commodity === sel && r.frequency === freq), [rows, sel, freq]);
  const regions = uniq(series.map((r) => r.region));
  const unit = series[0]?.unit || "";

  const chart = useMemo(() => {
    const m = {};
    series.forEach((r) => {
      m[r.date] = m[r.date] || { date: r.date };
      m[r.date][r.region] = r.price;
    });
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date)).map((o) => ({ ...o, label: freq === "Monthly" ? o.date.slice(0, 7) : o.date.slice(5) }));
  }, [series, freq]);

  const first = chart[0] || {};
  const last = chart[chart.length - 1] || {};
  const spread = regions.map((rg) => ({ region: rg, price: last[rg] })).sort((a, b) => b.price - a.price);
  const avgLast = regions.length ? regions.reduce((a, rg) => a + (last[rg] || 0), 0) / regions.length : 0;
  const avgFirst = regions.length ? regions.reduce((a, rg) => a + (first[rg] || 0), 0) / regions.length : 0;
  const vals = series.map((r) => r.price);
  const mean = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
  const vol = Math.sqrt(vals.reduce((a, v) => a + (v - mean) ** 2, 0) / (vals.length || 1)) / (mean || 1) * 100;

  const table = useMemo(() => regions.map((rg) => {
    const v = series.filter((r) => r.region === rg).map((r) => r.price);
    return { region: rg, commodity: sel, unit, latest: last[rg], min: Math.min(...v), max: Math.max(...v), avg: v.reduce((a, b) => a + b, 0) / v.length, chg: first[rg] ? ((last[rg] / first[rg]) - 1) * 100 : 0 };
  }), [regions, series, sel, unit, last, first]);

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Module B · Price Trends" title="Chemicals & Utilities Prices" desc="Regional price assessments — monthly history and daily granularity where available.">
        <Segmented testId="prices-category-toggle" value={category} onChange={setCategory} options={[{ value: "Chemical", label: "Chemicals" }, { value: "Utility", label: "Utilities" }]} />
        {category === "Chemical" ? selector : <FilterSelect testId="prices-commodity-select" value={sel || ""} onChange={setCommodity} options={commodities} allLabel={null} />}
        <Segmented testId="prices-timeframe-toggle" value={freq} onChange={setFreq} options={freqs.map((f) => ({ value: f, label: f }))} />
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="prices-kpi-avg" label={`${sel} · avg latest`} value={fmt(avgLast, 2)} unit={unit} />
        <Kpi testId="prices-kpi-change" label="Period change" value={fmt(avgFirst ? (avgLast / avgFirst - 1) * 100 : 0, 1)} unit="%" accent={COLORS.orange} />
        <Kpi testId="prices-kpi-volatility" label="Volatility (CoV)" value={fmt(vol, 1)} unit="%" accent={COLORS.cyan} />
        <Kpi testId="prices-kpi-spread" label="Max regional spread" value={fmt((spread[0]?.price || 0) - (spread[spread.length - 1]?.price || 0), 2)} unit={unit} accent={COLORS.blue} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" title={`${sel} · ${freq} Price Trend`} sub={`${unit} by region · ${chart[0]?.date || ""} → ${last.date || ""}`} testId="prices-trend-line-chart">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chart}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis dataKey="label" stroke="#334155" minTickGap={24} />
              <YAxis stroke="#334155" domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip unit={unit} digits={2} />} />
              <Legend />
              {regions.map((rg, i) => (
                <Line key={rg} dataKey={rg} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <AIInsights module="prices" product={product} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Regional Spread · Latest" sub={`${sel}, ${unit}`} testId="prices-spread-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={spread}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis dataKey="region" stroke="#334155" interval={0} tick={{ fontSize: 10 }} />
              <YAxis stroke="#334155" />
              <Tooltip content={<ChartTooltip unit={unit} digits={2} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="price" name="Price" radius={[4, 4, 0, 0]}>
                {spread.map((s, i) => <Cell key={s.region} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="lg:col-span-2" title="Price Statistics" sub={`${freq} min / max / avg by region`}>
          <DataTable
            testId="prices-data-table"
            pageSize={8}
            rows={table}
            columns={[
              { key: "region", label: "Region" },
              { key: "latest", label: "Latest", num: true, render: (r) => fmt(r.latest, 2) },
              { key: "min", label: "Min", num: true, render: (r) => fmt(r.min, 2) },
              { key: "max", label: "Max", num: true, render: (r) => fmt(r.max, 2) },
              { key: "avg", label: "Avg", num: true, render: (r) => fmt(r.avg, 2) },
              { key: "chg", label: "Δ %", num: true, render: (r) => <span className={r.chg >= 0 ? "text-[#03AB9E]" : "text-red-400"}>{fmt(r.chg, 1)}%</span> },
              { key: "unit", label: "Unit" },
            ]}
          />
        </Panel>
      </div>

      <Panel title="Raw Price Records" sub={`${sel} · ${freq}`}>
        <DataTable
          testId="prices-raw-table"
          rows={[...series].sort((a, b) => b.date.localeCompare(a.date))}
          columns={[
            { key: "date", label: "Date" },
            { key: "region", label: "Region" },
            { key: "commodity", label: "Commodity" },
            { key: "price", label: "Price", num: true, render: (r) => fmt(r.price, 2) },
            { key: "unit", label: "Unit" },
          ]}
        />
      </Panel>
    </div>
  );
}

export default function Prices() {
  return <ProductGate module="prices" eyebrow="Price Trends" title="Price Trends">{(p) => <PricesView {...p} />}</ProductGate>;
}
